import { useState, type ReactNode } from "react";
import type { IconName } from "../icons";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Icon } from "./icon";

export type BannerTone = "info" | "success" | "warning" | "danger" | "accent";

const TONE: Record<BannerTone, { box: string; icon: IconName; iconClass: string }> = {
  info: { box: "border-line bg-info-soft", icon: "info", iconClass: "text-info" },
  success: { box: "border-line bg-success-soft", icon: "check-circle", iconClass: "text-success" },
  warning: { box: "border-line bg-warning-soft", icon: "alert-triangle", iconClass: "text-warning" },
  danger: { box: "border-line bg-danger-soft", icon: "alert-circle", iconClass: "text-danger" },
  accent: { box: "border-line bg-accent-soft", icon: "sparkles", iconClass: "text-accent-ink" },
};

const STORAGE_PREFIX = "sg-dismissed:";

function readDismissed(key: string | undefined): boolean {
  if (!key) return false;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(key: string | undefined): void {
  if (!key) return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, "1");
  } catch {
    // Storage blocked (private mode / in-app browser): dismissal lasts for this view only.
  }
}

export interface DismissibleBannerProps {
  title: ReactNode;
  children?: ReactNode;
  tone?: BannerTone;
  icon?: IconName;
  /** Persist dismissal per browser (prefixed `sg-dismissed:`). Server-side per-user persistence goes through `onDismiss`. */
  storageKey?: string;
  onDismiss?: () => void;
  dismissLabel?: string;
  actions?: ReactNode;
  className?: string;
}

/** `[data-dismissible]` box: icon + title + text + optional actions + close button. */
export function DismissibleBanner({
  title,
  children,
  tone = "info",
  icon,
  storageKey,
  onDismiss,
  dismissLabel = "Dismiss",
  actions,
  className,
}: DismissibleBannerProps) {
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));
  if (dismissed) return null;
  const spec = TONE[tone];
  return (
    <section aria-label={typeof title === "string" ? title : undefined} className={cn("flex gap-3 rounded-lg border p-4", spec.box, className)}>
      <Icon name={icon ?? spec.icon} className={cn("mt-0.5 size-5", spec.iconClass)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-fg">{title}</p>
        {children ? <div className="mt-1 text-sm text-fg-muted">{children}</div> : null}
        {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={dismissLabel}
        onClick={() => {
          writeDismissed(storageKey);
          setDismissed(true);
          onDismiss?.();
        }}
      >
        <Icon name="x" />
      </Button>
    </section>
  );
}
